import type { FlagManager } from './useFlag';
import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { percentToBp } from './flag-drafts';
import { publishStart } from './flag-status';
import { rolloutFigure } from './rollout-figure';

type RolloutControlCopy = { saved: string };

/**
 * The rollout control: a share typed or slid, saved on Save. A change by hand pauses a running
 * plan (rollout-handler.ts), which the card says before it happens. Turn off is the page's main
 * action, in the breadcrumb bar (FlagHeader), not here.
 *
 * The slider and the field are one draft, so each follows the other; the card's figure follows
 * the draft too (rolloutFigure), marked as not saved, with what is live now beside it.
 */
export const useRolloutControl = (manager: FlagManager, copy: RolloutControlCopy) => {
  const savedBp = manager.rollout?.rolloutBp ?? 0;
  const holdbackBp = manager.rollout?.holdbackBp ?? 0;
  const [draft, setDraft] = useState<string | null>(null);

  const text = draft ?? String(savedBp / 100);
  const draftBp = percentToBp(text);
  const isDirty = draftBp !== null && draftBp !== savedBp;
  const isBusy = manager.pending === 'rollout';
  const canEdit = manager.canChange && !manager.isArchived;
  const planActive = manager.rollout?.planStatus === 'active';
  const pausesPlan = planActive && isDirty;
  // A draft with a plan, or an editor draft left at 0%, does not go live at the share typed here
  // (publish-flag.ts): the card says so where the share is set.
  const draftStart = manager.isDraft && manager.rollout ? publishStart(manager.rollout) : null;
  const figure = rolloutFigure({
    savedBp,
    draftBp: draft === null ? null : draftBp,
    holdbackBp,
    isArchived: manager.isArchived,
  });

  const save = async () => {
    if (draftBp === null || !isDirty) return;
    // The share this control showed: a scheduler step or another person's change since makes
    // the API refuse it rather than overwrite it.
    if (await manager.setRollout({ rolloutBp: draftBp, expectedBp: savedBp })) {
      setDraft(null);
      toast.success(copy.saved);
    }
  };

  return {
    savedBp,
    holdbackBp,
    figure,
    text,
    setText: (value: string) => setDraft(value),
    setFromSlider: (percent: number) => setDraft(String(percent)),
    // The slider shows the draft while it is a share, the saved value otherwise.
    sliderPercent: (draftBp ?? savedBp) / 100,
    isInvalid: draftBp === null,
    isDirty,
    // A typed value that is not a share yet still has something to discard.
    hasDraft: draft !== null && (isDirty || draftBp === null),
    isBusy,
    canEdit,
    pausesPlan,
    draftStart,
    save,
    discard: () => setDraft(null),
  };
};

export type RolloutControlManager = ReturnType<typeof useRolloutControl>;
